/**
 * `ChannelThread` — one channel conversation, styled to LOOK AND FEEL like the
 * real messenger web client (operator direction 2026-07-22/23): pixel-close to
 * Telegram/WhatsApp Web. Oldest-first, read-only. Sides mirror the LAWYER's own
 * client (operator flip 2026-07-23): the human PEER (`role:"user"`) sits RIGHT
 * in the channel's outgoing colour with the delivered-check; the AGENT
 * (`role:"assistant"`) sits LEFT in the incoming (white) colour.
 *
 * ## One component, two grammars
 * Everything channel-specific is data on the `MessengerTheme` grammar record
 * (`messenger-theme.ts`) plus the icon sets in `messenger-chrome.ts` — there is
 * no forked WhatsApp thread. The structural difference the record encodes:
 * Telegram tails the BOTTOM-outer corner of the LAST bubble of a sender-run,
 * WhatsApp the TOP-outer corner of the FIRST. Both predicates fall out of the
 * grouping the list already computes.
 *
 * Faithful details: a chat header (avatar · name · subtitle · the channel's own
 * action icons), centered day-separator pills ("Hoje"/"Ontem"/date, uppercased
 * on WhatsApp), the timestamp INLINE on the last text line (both clients
 * reserve room for it rather than dropping it to its own row), a single "sent"
 * check on the holder's messages (read receipts aren't in the `channels.*`
 * store, so never a double/blue check), light inline markdown, and a composer
 * that replicates the messenger's input with the read-only lock notice overlaid
 * on it. That notice is honest but no longer forced: `channels.reply` (an
 * attributed lawyer write) exists as of 2026-07-23, and binding it is tabs-plan
 * P3 — a separately-approved slice. Until then this surface reads, and says so.
 *
 * WhatsApp's end-to-end-encryption notice occupies a system-pill slot at the
 * top of every conversation. We take the SLOT and its treatment but never its
 * claim: this pane mirrors the agent's traffic, so it carries a truthful
 * read-only notice instead.
 *
 * In a 1:1 chat the sender is implied by side + colour, so bubbles carry NO
 * per-message name or avatar (those live in the header) — matching the clients.
 *
 * See `ai-docs/lexide-agent-channel-tabs-impl-plan.md` §5.3 +
 * `ai-docs/lexide-whatsapp-tab-miniplan.md` (WA-2).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Loader2, Lock } from "lucide-react";

import { useChannelThread } from "@/hooks/useChannels";
import type { ChannelConversation, ChannelMessage } from "@/api/lx-channels";
import { channelScopeToMediaParams } from "@/lib/channel-scope";
import {
  AttachmentEchoChip,
  type EchoAttachment,
} from "@/components/workspace/attachment-echo";
import { PeerAvatar, PeerTierBadge } from "@/components/agent/PeerAvatar";
import {
  getMessengerTheme,
  sameDay,
  dayLabel,
  parseInline,
  type MessengerTheme,
} from "@/components/agent/messenger-theme";
import { getMessengerChrome } from "@/components/agent/messenger-chrome";
import type { PeerContact } from "@/hooks/useContactPeerNames";
import { cn } from "@/lib/utils";

/** How many of the most-recent messages a thread renders before you ask for
 *  more — a real messenger opens on the tail, not the top of history. "Load
 *  older" first reveals the rest of the already-loaded page, then pages back to
 *  the server. Keeps the initial DOM small on long threads. */
const INITIAL_TAIL_CAP = 50;

export function ChannelThread({
  channel,
  conversation,
  onBack,
  resolvePeerName,
  showBack = true,
}: {
  channel: string;
  conversation: ChannelConversation;
  onBack: () => void;
  /** Peer → curated contact identity (contacts join). Absent/`undefined` ⇒
   *  the header degrades to `peerName`, then the raw peer. */
  resolvePeerName?: (channel: string, peer: string | null | undefined) => PeerContact | undefined;
  /** Show the back button (single-column mode). Hidden in the two-column
   *  layout where the list never left the screen. */
  showBack?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { messages, hasMore, isLoading, isLoadingMore, error, loadOlder } =
    useChannelThread(channel, conversation.peer, { enabled: true });
  const theme = getMessengerTheme(channel);
  const chrome = getMessengerChrome(channel);
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { timeStyle: "short" }),
    [i18n.language],
  );

  // This is a TELEGRAM/WhatsApp screen, so the account's OWN name leads (operator
  // 2026-07-23): the PRIMARY is the peer's messenger username (`peerName`, e.g.
  // "Claudio"); the Lexiun curated contact name (e.g. "Alexandre Moraes") is the
  // SECONDARY, small — deliberately surfacing the messenger↔Lexiun mismatch. The
  // username is stamped by the runtime; where it is missing the primary falls
  // back to the curated name, then the raw chat id.
  const contact = resolvePeerName?.(channel, conversation.peer);
  const messengerName = conversation.peerName?.trim();
  const lexiunName = contact?.name;
  const peerLabel =
    messengerName || lexiunName || conversation.peer || t("agent.channels.unknownSender");
  // The null-peer bucket aggregates unresolved peers → a neutral avatar, never
  // a monogram that reads as one person (the name text stays honest).
  const avatarLabel = conversation.peer === null ? "" : peerLabel;
  // Secondary (small): the Lexiun contact name when it isn't already the primary,
  // else the raw chat id. Never repeats what the primary already shows.
  const subtitle =
    lexiunName && lexiunName !== peerLabel
      ? lexiunName
      : conversation.peer && conversation.peer !== peerLabel
        ? conversation.peer
        : undefined;

  const now = Date.now();
  const dayLabels = {
    today: t("agent.channels.thread.today"),
    yesterday: t("agent.channels.thread.yesterday"),
  };

  // Render only the newest slice by default; "load older" lifts the cap to the
  // full loaded page before it pages back to the server.
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? messages : messages.slice(-INITIAL_TAIL_CAP);
  const hasHiddenOlder = !expanded && messages.length > INITIAL_TAIL_CAP;
  const showOlder = useCallback(() => {
    // reveal the rest of what's already loaded first, then fetch the next page
    if (messages.length > INITIAL_TAIL_CAP && !expanded) setExpanded(true);
    else void loadOlder();
  }, [messages.length, expanded, loadOlder]);

  // Auto-scroll to the latest message like a real messenger: on first load of a
  // thread and when a NEW tail message arrives while the reader is already at the
  // bottom. Loading OLDER prepends (the last message is unchanged) and expanding
  // the cap grows the list upward — neither yanks the viewport.
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const lastKeyRef = useRef("");
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  // Reset the tail window + re-pin to the bottom whenever the conversation changes.
  useEffect(() => {
    setExpanded(false);
    nearBottomRef.current = true;
    lastKeyRef.current = "";
  }, [conversation.peer]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    const last = messages[messages.length - 1];
    const lastKey = `${last.runId}:${last.role}:${last.ts}`;
    const isNewTailOrFirstLoad = lastKeyRef.current === "" || lastKeyRef.current !== lastKey;
    lastKeyRef.current = lastKey;
    if (isNewTailOrFirstLoad && nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    <div data-testid="channel-thread" className="flex min-h-0 flex-1 flex-col">
      {/* Chat header — avatar · name · subtitle · the channel's action icons. */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-2.5 border-b px-3 py-2",
          theme.headerClass,
        )}
      >
        {showBack && (
          <button
            type="button"
            data-testid="channel-thread-back"
            onClick={onBack}
            aria-label={t("agent.channels.thread.back")}
            className="-ml-1 shrink-0 rounded-full p-1.5 text-coffee-600 transition-colors hover:bg-cream-100 hover:text-coffee-900 dark:text-cream-400 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <PeerAvatar label={avatarLabel} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className="truncate text-body-sm font-semibold text-coffee-900 dark:text-cream-100"
              title={conversation.peer ?? undefined}
            >
              {peerLabel}
            </span>
            {contact?.tier && <PeerTierBadge tier={contact.tier} />}
          </div>
          {subtitle && (
            <div className="truncate text-caption text-cream-500">{subtitle}</div>
          )}
        </div>
        {/* Decorative chrome to complete the messenger header (read-only surface):
            the icon SET is the channel's own — WhatsApp carries call affordances
            Telegram does not. */}
        {chrome.headerIcons.map((Icon, i) => (
          <Icon key={i} size={18} aria-hidden className="shrink-0 text-cream-500" />
        ))}
      </div>

      {/* Chat body — wallpaper behind the bubbles. */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={cn("min-h-0 flex-1 overflow-y-auto px-3 py-3", theme.wallpaperClass)}
      >
        {(hasHiddenOlder || hasMore) && (
          <div className="mb-3 flex justify-center">
            <button
              type="button"
              data-testid="channel-load-older"
              onClick={showOlder}
              disabled={isLoadingMore}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-caption text-coffee-600 shadow-sm backdrop-blur transition-colors hover:bg-white disabled:opacity-50 dark:bg-coffee-800/80 dark:text-cream-300 dark:hover:bg-coffee-800"
            >
              {isLoadingMore && <Loader2 size={12} className="animate-spin" />}
              {t("agent.channels.thread.loadOlder")}
            </button>
          </div>
        )}

        {/* The system-notice slot the real client opens a conversation with —
            shown only once the start of the thread is actually on screen, and
            carrying a TRUTHFUL notice: this is a read-only mirror, not an
            end-to-end-encrypted session, and claiming otherwise to a lawyer
            would be a lie. */}
        {theme.systemPillClass && !hasMore && !hasHiddenOlder && !isLoading && messages.length > 0 && (
          <div className="mb-3 flex justify-center">
            <span
              data-testid="channel-system-pill"
              className={cn(
                "flex max-w-[85%] items-center gap-1.5 px-3 py-1.5 text-center text-[11px] leading-snug",
                theme.systemPillClass,
              )}
            >
              <Lock size={11} aria-hidden className="shrink-0" />
              {t("agent.channels.thread.mirrorNotice")}
            </span>
          </div>
        )}

        {isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center py-16 text-cream-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {error && messages.length === 0 && (
          <div
            data-testid="channel-thread-error"
            role="alert"
            className="m-3 rounded border border-red-200 bg-red-50 p-3 text-body-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {t("agent.channels.loadFailed", { message: error })}
          </div>
        )}

        {!isLoading && !error && messages.length === 0 && (
          <div data-testid="channel-thread-empty" className="flex justify-center py-16">
            {/* The pill treatment, but NOT the uppercase transform — that
                belongs to short day labels, not to a sentence. */}
            <span className={cn("px-3 py-1 text-caption", theme.dayPillClass)}>
              {t("agent.channels.thread.empty")}
            </span>
          </div>
        )}

        <ul className="flex flex-col">
          {shown.map((m, i) => {
            const prev = shown[i - 1];
            const next = shown[i + 1];
            const newDay = !prev || !sameDay(prev.ts, m.ts);
            const senderChanged = !prev || prev.role !== m.role;
            // Same sender as the bubble above, same day → hug it (grouping).
            const tightTop = !newDay && !senderChanged;
            // Last bubble of a sender-run — Telegram's tail anchor. WhatsApp
            // tails the FIRST of the run instead, which is exactly `!tightTop`.
            const tailBottom = !next || next.role !== m.role || !sameDay(next.ts, m.ts);
            return (
              <Fragment key={`${m.runId}:${m.role}:${m.ts}`}>
                {newDay && (
                  <li className="my-2 flex justify-center">
                    <span
                      className={cn(
                        "px-2.5 py-0.5 text-[11px] font-medium",
                        theme.dayPillClass,
                        theme.dayPillUpperClass,
                      )}
                    >
                      {dayLabel(m.ts, now, i18n.language, dayLabels)}
                    </span>
                  </li>
                )}
                <ChannelBubble
                  message={m}
                  theme={theme}
                  timeFmt={timeFmt}
                  tightTop={tightTop}
                  tailed={theme.tailEdge === "top" ? !tightTop : tailBottom}
                />
              </Fragment>
            );
          })}
        </ul>
      </div>

      {/* Composer row — replicates the messenger's input, honestly read-only. */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 border-t px-3 py-2",
          theme.composerBarClass,
        )}
      >
        {chrome.composerLeadIcons.map((Icon, i) => (
          <Icon key={i} size={20} aria-hidden className="shrink-0 text-cream-500" />
        ))}
        <div
          data-testid="channel-readonly-note"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 px-3.5 py-2 text-caption text-cream-500",
            theme.composerInputClass,
          )}
        >
          <Lock size={13} className="shrink-0" />
          <span className="truncate">{t("agent.channels.thread.readOnlyComposer")}</span>
        </div>
        {chrome.composerTrailIcons.map((Icon, i) => (
          <Icon key={i} size={20} aria-hidden className="shrink-0 text-cream-500" />
        ))}
      </div>
    </div>
  );
}

/** Renders the agent's light inline markdown (bold/italic/code) as plain text
 *  nodes — like the real clients, and injection-safe (never HTML). */
function MessageText({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((s, i) => {
        if (s.type === "bold") return <strong key={i} className="font-semibold">{s.value}</strong>;
        if (s.type === "italic") return <em key={i}>{s.value}</em>;
        if (s.type === "code")
          return (
            <code key={i} className="rounded bg-black/10 px-1 text-[0.85em] dark:bg-white/15">
              {s.value}
            </code>
          );
        return <Fragment key={i}>{s.value}</Fragment>;
      })}
    </>
  );
}

/** The in-bubble timestamp (+ the single "sent" check on the holder's own
 *  messages). Shared by both meta layouts so they can never drift. */
function BubbleMeta({
  message,
  timeFmt,
  onRight,
  className,
}: {
  message: ChannelMessage;
  timeFmt: Intl.DateTimeFormat;
  onRight: boolean;
  className?: string;
}) {
  return (
    <span className={cn("flex items-center gap-0.5 leading-none", className)}>
      {timeFmt.format(message.ts)}
      {/* Delivered-check on the account holder's own (right) messages, where
          both clients show it; SINGLE tick only — read receipts (WhatsApp's
          double / blue tick) aren't in the `channels.*` store, and inventing
          them would claim a delivery state we cannot know. */}
      {onRight && <Check size={13} aria-hidden className="-mr-0.5" />}
    </span>
  );
}

function ChannelBubble({
  message,
  theme,
  timeFmt,
  tightTop,
  tailed,
}: {
  message: ChannelMessage;
  theme: MessengerTheme;
  timeFmt: Intl.DateTimeFormat;
  /** Same sender as the message above → hug it (messenger grouping). */
  tightTop: boolean;
  /** This bubble carries the tail (which end of the run depends on the
   *  channel's `tailEdge`). */
  tailed: boolean;
}) {
  // The agent is ALWAYS on the left; the account holder (the human peer) is
  // ALWAYS on the right (operator rule 2026-07-23) — mirrors the lawyer's own
  // client, where their side is the right one in the outgoing colour.
  const onRight = message.role === "user";
  const scopeParams = channelScopeToMediaParams(message.scope);
  const attachments: EchoAttachment[] = (message.attachments ?? []).map((a) => ({
    name: a.name,
    ...(a.mime ? { mime: a.mime } : {}),
    ...(scopeParams ? { fetch: { ...scopeParams, name: a.name } } : {}),
  }));
  const metaClass = onRight ? theme.metaOutClass : theme.metaInClass;
  // Both clients float the meta onto the last line of TEXT, reserving room for
  // it with an invisible inline spacer. Blocks that own their own layout
  // (attachments, an error line) keep the meta on its own row instead, where a
  // floated one would overlap.
  const inlineMeta = !!message.text && attachments.length === 0 && !message.error;

  return (
    <li
      className={cn(
        "flex",
        onRight ? "justify-end" : "justify-start",
        tightTop ? theme.tightGapClass : theme.runGapClass,
      )}
    >
      <div
        className={cn(
          "relative flex flex-col text-body-sm",
          theme.paddingClass,
          theme.maxWidthClass,
          theme.radiusClass,
          theme.shadowClass,
          onRight
            ? cn(theme.outBubbleClass, tailed && theme.tailOutClass)
            : cn(theme.inBubbleClass, tailed && theme.tailInClass),
        )}
      >
        {message.text && (
          <span className="whitespace-pre-wrap break-words">
            <MessageText text={message.text} />
            {inlineMeta && (
              // Invisible copy of the meta, inline at the end of the text: it
              // reserves EXACTLY the room the floated meta needs, in any locale
              // and time format, so short messages sit beside their timestamp
              // and long ones push it to the next line — as both clients do.
              <span aria-hidden className="pointer-events-none inline-flex select-none align-bottom opacity-0">
                &nbsp;&nbsp;
                <BubbleMeta
                  message={message}
                  timeFmt={timeFmt}
                  onRight={onRight}
                  className={metaClass}
                />
              </span>
            )}
          </span>
        )}
        {message.error && (
          <span className="mt-1 text-caption italic text-red-600 dark:text-red-400">
            {message.error}
          </span>
        )}
        {attachments.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1">
            {attachments.map((a) => (
              <AttachmentEchoChip key={a.name} attachment={a} />
            ))}
          </div>
        )}
        <BubbleMeta
          message={message}
          timeFmt={timeFmt}
          onRight={onRight}
          className={cn(
            metaClass,
            inlineMeta ? cn("absolute", theme.metaInsetClass) : "mt-0.5 self-end",
          )}
        />
      </div>
    </li>
  );
}
